import { type FC, type ReactNode } from 'react';

import { Box } from '../structural/Box';
import { ConditionallyRender } from '../util/ConditionallyRender';

import { AppRoot } from './AppRoot';
import { Content } from './Content';
import { Main } from './Main';

export interface LayoutProps {
  readonly children?: ReactNode;
  /**
   * Defines the optional header element for the layout.
   *
   * Eventually, this prop may be deprecated once the header element is migrated into the
   * '@craft/ui' package.
   */
  readonly header?: ReactNode;
  readonly isHeaderVisible?: boolean;
  readonly isPaddingDisabled?: boolean;
  readonly isScrollable?: boolean;
  /**
   * Defines whether or not the sidebar is visible in the layout.  By default, the sidebar will
   * always be rendered unless the layout is marked as public via the {@link isPublic} prop. This
   * prop can be used to explicitly hide the sidebar even when the layout is not public.
   *
   * @default true
   */
  readonly isSidebarVisible?: boolean;
  /**
   * Defines the optional sidebar element for the layout.
   *
   * Eventually, this prop may be deprecated once the sidebar element is fully migrated into the
   * '@craft/ui' package.
   */
  readonly sidebar?: ReactNode;
}

export const Layout: FC<LayoutProps> = ({
  children,
  header,
  isHeaderVisible = true,
  isPaddingDisabled = false,
  isScrollable = false,
  isSidebarVisible = true,
  sidebar,
}) => (
  <AppRoot>
    <ConditionallyRender show={isHeaderVisible}>{header}</ConditionallyRender>
    <Content>
      <ConditionallyRender show={isSidebarVisible}>
        <Box sx={{ display: { sm: 'contents', xs: 'none' } }}>{sidebar}</Box>
      </ConditionallyRender>
      <Main
        isPaddingDisabled={isPaddingDisabled}
        isScrollable={isScrollable}
        isSidebarVisible={isSidebarVisible}
      >
        {children}
      </Main>
    </Content>
  </AppRoot>
);
